/* eslint-disable @typescript-eslint/no-explicit-any */
import type { BootcampDelivery, NucampUser } from "$lib/datamodel";
import { getCookie, nucampTracking, queryParamMap } from "./window";
import { get } from 'svelte/store';
import { geolocation } from "../stores/nucamp-geolocation";

function generateEventID() {
  return Math.random().toString(36).substring(2, 9).toUpperCase() + '-' + Math.random().toString(36).substring(2, 9).toUpperCase();
}

function setPayload(data: any, user?: NucampUser) {
  const eventID = generateEventID();
  const payload = {...data, eventID};
  if (user && user.uid) payload.external_id = user.uid;
  else if (user && user.id) payload.external_id = user.id;
  return payload;
}

export async function followBootcampEvent(bootcamp: BootcampDelivery, value: number, user?: NucampUser, medium?: string, keyword?: string) {
  const contentids = [bootcamp.id, `bootcamp-${bootcamp.bootcamp.id}`];
  const clientPayload = setPayload({
    'event': 'follow_bootcamp',
    'bootcampid': bootcamp.bootcamp.id,
    'contentids': contentids,
    'label': bootcamp.id,
    'communityid': bootcamp.community.id,
    'community': bootcamp.community.urlId,
    'bootcamptitle': bootcamp.title,
    'tuition': value,
    'items': [{
      item_id: bootcamp.id,
      item_name: bootcamp.title,
      item_category: bootcamp.title,
      item_variant: bootcamp.community.community.name + ", " + bootcamp.community.community.state,
      price: value,
      quantity: 1,
    }],
  }, user);
  const fbServerPayload = {
    event_name: 'ViewContent',
    event_id: clientPayload.eventID,
    custom_data: {
      content_ids: contentids,
      content_name: bootcamp.title,
      content_type: 'product',
      value: 10,
      currency: 'USD'
    },
  }
  nucampTracking({medium: medium ?? "", content: bootcamp.id, campaign: "followbootcamp", keyword: keyword ?? ""});
  await Promise.all([
    sendClientEvent(clientPayload, user).catch(),
    sendServerEvent(fbServerPayload, user).catch(),
    browseWebsite(user)
  ]);
}

export async function browseWebsite(user?: NucampUser) {
  const clientPayload = setPayload({
    'event': 'browse',
  }, user);
  const fbServerPayload = {
    event_name: 'ViewContent',
    event_id: clientPayload.eventID,
    custom_data: {
      content_name: 'browse',
      value: 0.1,
      currency: 'USD'
    }
  }
  await Promise.all([
    sendClientEvent(clientPayload, user).catch(),
    sendServerEvent(fbServerPayload, user).catch()
  ]);
}

async function sendClientEvent(data: any, loggedUser?: NucampUser){
  const payload = {...data};
  if (loggedUser) {
    if (loggedUser.email) payload.email = loggedUser.email;
    if (loggedUser.phoneId) {
      payload.phone_number = loggedUser.phoneId;
    } else if (loggedUser.phone) {
      payload.phone_number = loggedUser.phone;
    }
    if (loggedUser.lastname) payload.last_name = loggedUser.lastname;
    if (loggedUser.firstname) payload.first_name = loggedUser.firstname;
    if (loggedUser.location) payload.city = loggedUser.location;
    if (loggedUser.state) payload.region = loggedUser.state;
    
    const addressData = getGeoData(loggedUser);
    
    if (addressData.region) payload.region = addressData.region;
    if (addressData.city) payload.city = addressData.city;
    if (addressData.zipcode) payload.zipcode = addressData.zipcode;
    if (addressData.country) payload.country = addressData.country;
    if (addressData.street) payload.street = addressData.street;
  }
  pushTagToDOM(payload);
}

async function sendServerEvent(data: any, loggedUser: NucampUser | undefined) {
  const fbc = getCookie("_fbc");
  const fbp = getCookie("_fbp");
  const ttp = getCookie("_ttp");
  const ttclid = queryParamMap("ttclid");

  const payload:any = {
    event_name: data.event_name,
    event_id: data.event_id,
    user_data: {
      client_user_agent: window.navigator.userAgent,
      callback: ttclid,
    },
    action_source: 'website',
    event_source_url: window.location.href,
    custom_data: data.custom_data,
  }
  const currentGeoLocation = get(geolocation);
  if (currentGeoLocation?.ip) {
    payload.user_data.client_ip_address = currentGeoLocation.ip;
  }
  if (data.properties) {
    payload.properties = data.properties;
  }
  if (loggedUser) {
    if (loggedUser.email) payload.user_data.em = loggedUser.email;
    if (loggedUser.phoneId) {
      payload.user_data.ph = loggedUser.phoneId;
    } else if (loggedUser.phone) {
      payload.user_data.ph = loggedUser.phone;
    }
    if (loggedUser.lastname) payload.user_data.ln = loggedUser.lastname;
    if (loggedUser.firstname) payload.user_data.fn = loggedUser.firstname;
    if (loggedUser.location) payload.user_data.ct = loggedUser.location; // city
    if (loggedUser.state) payload.user_data.st = loggedUser.state; // state
    if (loggedUser.uid) {
      payload.user_data.external_id = loggedUser.uid;
    } else if (loggedUser.id) {
      payload.user_data.external_id = loggedUser.id;
    }
  }
  if (fbc) payload.user_data.fbc = fbc;
  if (fbp) payload.user_data.fbp = fbp;
  if (ttp) payload.user_data.ttp = ttp;

  await fetch(`/api/pixel/conversion`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload)
  })
    .catch((err) => {
      console.log("Server-side Pixel Error", err);
      return Promise.resolve();
    });
}

export function getGeoData(user: NucampUser) {
  const data = {
    street: "",
    city: "",
    region: "",
    country: "",
    zipcode: ""
  }

  if (user && user.fulladdress && Array.isArray(user.fulladdress)) {
    const countryData = user.fulladdress.find((address) => address.types.includes("country"));
    const zipcodeData = user.fulladdress.find((address) => address.types.includes("postal_code"));
    const stateData = user.fulladdress.find((address) => address.types.includes("administrative_area_level_1"));
    const cityData = user.fulladdress.find((address) => address.types.includes("locality"));
    const streetPremiseData = user.fulladdress.find((address) => address.types.includes("premise"));
    const streetSubPremiseData = user.fulladdress.find((address) => address.types.includes("subpremise"));
    const streetNumberData = user.fulladdress.find((address) => address.types.includes("street_number"));
    const streetRouteData = user.fulladdress.find((address) => address.types.includes("route"));
    if (countryData) {
      data.country = countryData.short_name;
    }
    if (zipcodeData) {
      data.zipcode = zipcodeData.long_name;
    }
    if (stateData) {
      data.region = stateData.short_name;
    }
    if (cityData) {
      data.city = cityData.long_name;
    }
    if (streetNumberData) {
      data.street = data.street + " " + streetNumberData.long_name;
    }
    if (streetRouteData) {
      data.street = data.street + " " + streetRouteData.long_name;
    }
    if (streetPremiseData) {
      data.street = data.street + " " + streetPremiseData.long_name;
    }
    if (streetSubPremiseData) {
      data.street = data.street + " " + streetSubPremiseData.long_name;
    }
  }
  return data;
}

export async function analyticsEvent (category: any, action: any, label: any) {
  const payLoad = setPayload(
    {
      'event': 'Interaction',
      'category': category,
      'action': action,
      'label': label
    }
  )
  pushTagToDOM(payLoad);
}

function pushTagToDOM(payload: any) {
  try {
    if (window?.dataLayer) {
      window.dataLayer.push(payload);
    } else {
      // console.error("gtmAddedToDom not yet added, not pushing this payload", payload);
    }
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (error) {
    // console.error("Error pushing payload to dataLayer", error);
  }
}

export async function captureUserEmail (user: NucampUser, type: "business" | "consumer") {
  let clientPayload;
  let fbServerPayload;
  if (type === "consumer") {
    clientPayload = setPayload({
      'event': 'lead_email'
    }, user);
    fbServerPayload = {
      event_name: 'Lead',
      event_id: clientPayload.eventID,
      custom_data: {
        value: 30,
        currency: 'USD'
      }
    }
  } else if (type === "business") {
    clientPayload = setPayload({
      'event': 'business_lead_email',
    }, user);
    fbServerPayload = {
      event_name: 'Lead',
      event_id: clientPayload.eventID,
      custom_data: {
        value: 100,
        currency: 'USD'
      }
    }
  }
  await Promise.all([
    sendClientEvent(clientPayload, user).catch(),
    sendServerEvent(fbServerPayload, user).catch(),
    browseWebsite(user)
  ]);
}

export async function setUserData(user: NucampUser) {
  const dataPush: any = {};
  if (user.email) dataPush.email = user.email.toLowerCase().trim();
  if (user.phoneId) {
    dataPush.phoneId = user.phoneId;
    dataPush.phone_number = user.phoneId;
  }
  if (user.phone && !user.phoneId) dataPush.phoneId = user.phone;
  if (user.lastname) dataPush.last_name = user.lastname;
  if (user.firstname) dataPush.first_name = user.firstname;
  if (user.location) dataPush.city = user.location;
  if (user.state) dataPush.region = user.state;

  const payload = setPayload(dataPush, user);
  pushTagToDOM(payload);
}